<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { Delete, X } from 'lucide-vue-next'

/**
 * Centred on-screen number pad for touch terminals.
 *
 * The parent owns the value (`v-model`) so the pad can be opened next to any
 * input without stealing focus from a hardware scanner.
 */
const props = withDefaults(
  defineProps<{
    modelValue: string
    title: string
    hint?: string
    doneLabel?: string
    clearLabel?: string
    backspaceLabel?: string
    closeLabel?: string
    maxLength?: number
  }>(),
  {
    hint: '',
    doneLabel: 'Done',
    clearLabel: 'Clear',
    backspaceLabel: 'Delete last digit',
    closeLabel: 'Close',
    maxLength: 32,
  },
)

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
  (event: 'confirm', value: string): void
  (event: 'close'): void
}>()

const DIGIT_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const

const value = ref(props.modelValue)

watch(
  () => props.modelValue,
  (next) => {
    value.value = next
  },
)

function press(key: string): void {
  if (key === 'back') value.value = value.value.slice(0, -1)
  else if (key === 'clear') value.value = ''
  else if (value.value.length < props.maxLength) value.value += key
  emit('update:modelValue', value.value)
}

function confirm(): void {
  emit('confirm', value.value)
  emit('close')
}

function close(): void {
  emit('close')
}

function onKeydown(event: KeyboardEvent): void {
  if (event.altKey || event.ctrlKey || event.metaKey) return
  if (/^[0-9]$/.test(event.key)) {
    event.preventDefault()
    press(event.key)
  } else if (event.key === 'Backspace') {
    event.preventDefault()
    press('back')
  } else if (event.key === 'Enter') {
    event.preventDefault()
    confirm()
  } else if (event.key === 'Escape') {
    event.preventDefault()
    close()
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <Teleport to="body">
    <div class="numpad-modal" @click.self="close">
      <div class="numpad" role="dialog" aria-modal="true" :aria-label="title">
        <header class="numpad__head">
          <div class="numpad__heading">
            <p class="numpad__title">{{ title }}</p>
            <p v-if="hint" class="numpad__hint">{{ hint }}</p>
          </div>
          <button type="button" class="numpad__close" :aria-label="closeLabel" @click="close">
            <X :size="20" aria-hidden="true" />
          </button>
        </header>

        <p class="numpad__value" :class="{ 'numpad__value--empty': !value }">{{ value || '0' }}</p>

        <div class="numpad__grid">
          <button
            v-for="key in DIGIT_KEYS"
            :key="key"
            type="button"
            class="numpad__key"
            @click="press(key)"
          >
            {{ key }}
          </button>
        </div>

        <div class="numpad__utility">
          <button type="button" class="numpad__key numpad__key--utility" @click="press('clear')">
            {{ clearLabel }}
          </button>
          <button type="button" class="numpad__key" @click="press('0')">0</button>
          <button
            type="button"
            class="numpad__key numpad__key--utility"
            :aria-label="backspaceLabel"
            @click="press('back')"
          >
            <Delete :size="24" aria-hidden="true" />
          </button>
        </div>

        <button type="button" class="numpad__done" @click="confirm">{{ doneLabel }}</button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.numpad-modal {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgb(15 23 20 / 62%);
}

.numpad {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: min(400px, 94vw);
  max-height: calc(100dvh - 40px);
  overflow-y: auto;
  padding: 18px;
  border: 1px solid #dfe8e5;
  border-radius: 16px;
  background: #fff;
}

.numpad__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.numpad__heading { min-width: 0; }
.numpad__title { margin: 0; color: #12211d; font-size: 16px; font-weight: 800; }
.numpad__hint { margin: 2px 0 0; color: #65736e; font-size: 12px; font-weight: 600; }

.numpad__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  min-width: 44px;
  height: 44px;
  border: 0;
  border-radius: 11px;
  color: #4a5a55;
  background: #f1f5f4;
  cursor: pointer;
}
.numpad__close:hover { background: #e4eeec; color: #147a8a; }

.numpad__value {
  margin: 0;
  padding: 10px 14px;
  border: 2px solid #b9cfca;
  border-radius: 12px;
  background: #f7fbfa;
  color: #12211d;
  font-family: monospace;
  font-size: 34px;
  font-weight: 800;
  line-height: 1.15;
  text-align: right;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.numpad__value--empty { color: #a6b4b0; }

.numpad__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.numpad__utility {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.numpad__key {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 62px;
  border: 1px solid #d4d8d5;
  border-radius: 12px;
  color: #12211d;
  background: #f6f8f7;
  font-size: 26px;
  font-weight: 800;
  cursor: pointer;
}
.numpad__key:hover { border-color: #147a8a; background: #eaf4f5; }
.numpad__key:active { background: #dcefef; border-color: #147a8a; }

.numpad__key--utility {
  color: #3c4a46;
  background: #eff3f2;
  font-size: 15px;
  font-weight: 700;
}
.numpad__key--utility:last-child { font-size: 20px; }

.numpad__done {
  min-height: 58px;
  border: 0;
  border-radius: 12px;
  color: #fff;
  background: #147a8a;
  font-size: 17px;
  font-weight: 800;
  cursor: pointer;
}
.numpad__done:hover { background: #0f6573; }

@media (orientation: portrait), (max-width: 1180px) {
  .numpad { width: min(460px, 96vw); padding: 16px; gap: 10px; }
  .numpad__key { min-height: 68px; font-size: 28px; }
  .numpad__key--utility { font-size: 16px; }
  .numpad__done { min-height: 62px; }
}

@media (max-height: 620px) {
  .numpad__key { min-height: 48px; font-size: 22px; }
  .numpad__value { font-size: 26px; padding: 6px 12px; }
}
</style>
